import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
// import "styles/global.scss";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import ClientAxios from './rest-api/client';
import { GetLandingPageMutation } from './rest-api/mutations/landing-page/get-landing-page';
import {
  setLandingPageState,
  setVideoPreviewDone,
} from './store/slices/landingPageSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store/store';
import { setIsSettingsLoading } from './store/slices/globalLoadingSlice';
import Loader from './components/Loader/Loader';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
const Default = lazy(() => import('./layouts/Default/Default'));
const FullPageScrollLayout = lazy(
  () => import('./layouts/FullPageScrollLayout/FullPageScrollLayout')
);
const Home = lazy(() => import('./screens/Home/Home'));
const Guide = lazy(() => import('./screens/Guide/Guide'));
const ProductDetail = lazy(
  () => import('./screens/ProductDetail/ProductDetail')
);
const ExploreAll = lazy(() => import('./screens/ExploreAll/ExploreAll'));
const Resources = lazy(() => import('./screens/Resources/Resources'));
const AboutUs = lazy(() => import('./screens/AboutUs/AboutUs'));
const DigitalShowroom = lazy(
  () => import('./screens/DigitalShowroom/DigitalShowroom')
);
const StyleDetail = lazy(() => import('./screens/StyleDetail/StyleDetail'));
const Careers = lazy(() => import('./screens/Careers/Careers'));
const Warranty = lazy(() => import('./screens/Warranty/Warranty'));
const PrivacyPolicy = lazy(
  () => import('./screens/PrivacyPolicy/PrivacyPolicy')
);
const Contact = lazy(() => import('./screens/Contact/Contact'));
const FlyPages = lazy(() => import('./screens/FlyPages/FlyPages'));

const GTM_HEAD_ID = 'vistamark-gtm-head';
const GTM_NOSCRIPT_ID = 'vistamark-gtm-noscript';

const RecaptchaProvider = ({ children }: { children: ReactNode }) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY as string}
  >
    {children}
  </GoogleReCaptchaProvider>
);

const App = () => {
  const dispatch = useDispatch();
  const injectedGtmHeadSignature = useRef<string | null>(null);
  const injectedGtmNoscriptSignature = useRef<string | null>(null);
  const { setting, token } = useSelector(
    (state: RootState) => state.landingPage
  );

  const fetchLandingPageData = async () => {
    try {
      const res = await GetLandingPageMutation();
      dispatch(setIsSettingsLoading(false));
      if (res.status && res.data) {
        dispatch(
          setLandingPageState({
            navigation: res.data.navigation,
            explorSection: res.data.explore_section,
            footer: res.data.footer,
            seo: res.data.seo,
            heroSection: res.data.hero_section,
            featuredProducts: res.data.featured_products,
            mapArea: res.data.map_area,
            startProcess: res.data.start_process,
            setting: res.data.setting,
            certifications: res.data.certifications
              ? JSON.parse(res.data.certifications)
              : [],
            popup_video_url: res.data.popup_video_url ?? '',
            popup_visibility: res.data.popup_visibility || 'false',
            flyPages: res.data.fly_pages
              ? res.data.fly_pages.filter((f) => !!f.page_visibility)
              : [],
          })
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!setting) {
      dispatch(setIsSettingsLoading(true));
    }
    if (token) {
      ClientAxios.defaults.headers['Authorization'] = 'Bearer ' + token;
    }
    fetchLandingPageData();
    dispatch(setVideoPreviewDone(false));
  }, []);

  const injectGTMScripts = () => {
    if (!setting) return;

    const headSignature = setting.google_tag_manager?.trim();
    if (headSignature) {
      const existingHeadScript = document.getElementById(
        GTM_HEAD_ID
      ) as HTMLScriptElement | null;

      if (
        existingHeadScript?.getAttribute('data-vistamark-gtm-signature') ===
        headSignature ||
        injectedGtmHeadSignature.current === headSignature
      ) {
        return;
      }

      if (existingHeadScript) {
        existingHeadScript.remove();
      }

      console.log('✅ Injecting GTM head script');
      const headContainer = document.createElement('div');
      headContainer.innerHTML = headSignature;
      const scripts = Array.from(headContainer.querySelectorAll('script'));

      if (scripts.length > 0) {
        scripts.forEach((script, index) => {
          const nextScript = document.createElement('script');
          Array.from(script.attributes).forEach((attribute) => {
            nextScript.setAttribute(attribute.name, attribute.value);
          });

          if (index === 0) {
            nextScript.id = GTM_HEAD_ID;
            nextScript.setAttribute(
              'data-vistamark-gtm-signature',
              headSignature
            );
          }

          if (!script.src) {
            nextScript.text = script.textContent ?? '';
          }

          document.head.appendChild(nextScript);
        });

        injectedGtmHeadSignature.current = headSignature;
        console.log('✅ GTM head script injected successfully');
      } else {
        console.warn('⚠️ No script tag found in google_tag_manager');
      }
    } else {
      console.warn('⚠️ google_tag_manager not found in settings');
    }

    const noscriptSignature = setting.google_tag_manager_noscript?.trim();
    if (noscriptSignature) {
      const existingNoscript = document.getElementById(
        GTM_NOSCRIPT_ID
      ) as HTMLElement | null;

      if (
        existingNoscript?.getAttribute('data-vistamark-gtm-signature') ===
        noscriptSignature ||
        injectedGtmNoscriptSignature.current === noscriptSignature
      ) {
        return;
      }

      if (existingNoscript) {
        existingNoscript.remove();
      }

      console.log('✅ Injecting GTM noscript tag');
      const bodyNoscript = document.createElement('div');
      bodyNoscript.innerHTML = noscriptSignature;
      const noscriptElement = bodyNoscript.querySelector('noscript');

      if (noscriptElement) {
        const nextNoscript = noscriptElement.cloneNode(true) as HTMLElement;
        nextNoscript.id = GTM_NOSCRIPT_ID;
        nextNoscript.setAttribute(
          'data-vistamark-gtm-signature',
          noscriptSignature
        );
        document.body.insertBefore(nextNoscript, document.body.firstChild);
        injectedGtmNoscriptSignature.current = noscriptSignature;
        console.log('✅ GTM noscript tag injected successfully');
      } else {
        console.warn('⚠️ No noscript tag found in google_tag_manager_noscript');
      }
    } else {
      console.warn('⚠️ google_tag_manager_noscript not found in settings');
    }
  };

  const applySettings = () => {
    if (!setting) return;

    const root = document.documentElement;

    // Update body text styles
    // --custom-body-fontFamily
    // --custom-body-font-size
    // --custom-body-font-weight
    // --custom-heading-fontFamily
    // --custom-h1-font-size
    // --custom-h1-font-weight
    // --custom-h2-font-size
    // --custom-h2-font-weight
    // --custom-h3-font-size
    // --custom-h3-font-weight
    // --custom-btn-font-size
    // --custom-btn-font-weight
    root.style.setProperty(
      '--custom-body-fontFamily',
      setting?.primary_font_family
    );
    root.style.setProperty(
      '--custom-body-font-size',
      `${setting?.text_styles.body.size}px`
    );
    root.style.setProperty(
      '--custom-body-font-weight',
      setting?.text_styles.body.weight
    );

    root.style.setProperty(
      '--custom-heading-fontFamily',
      setting?.secondary_font_family
    );

    // headings styles
    // h1
    root.style.setProperty(
      '--custom-h1-font-size',
      `${setting?.heading_styles.h1.size}px`
    );
    root.style.setProperty(
      '--custom-h1-font-weight',
      setting?.heading_styles.h1.weight
    );
    root.style.setProperty(
      '--custom-h2-font-size',
      `${setting?.heading_styles.h2.size}px`
    );
    root.style.setProperty(
      '--custom-h2-font-weight',
      setting?.heading_styles.h2.weight
    );
    root.style.setProperty(
      '--custom-h3-font-size',
      `${setting?.heading_styles.h3.size}px`
    );
    root.style.setProperty(
      '--custom-h3-font-weight',
      setting?.heading_styles.h3.weight
    );
    root.style.setProperty(
      '--custom-btn-font-size',
      `${setting?.text_styles.button.size}px`
    );
    root.style.setProperty(
      '--custom-btn-font-weight',
      setting?.text_styles.button.weight
    );
  };

  useEffect(() => {
    if (setting) {
      dispatch(setIsSettingsLoading(false));
      applySettings();
      injectGTMScripts();
    }
  }, [setting]);

  return (
    <>
      <Loader />
      <Suspense fallback={<Loader />}>
        <BrowserRouter>
          <Routes>
            {/* Use a route with <Outlet/> as children to wrap routes with layouts and to create gates */}
            <Route
              element={
                <Default>
                  <Outlet />
                </Default>
              }
            >
              <Route path="guide" element={<Guide />} />
              <Route path="product-detail/:id" element={<ProductDetail />} />
              <Route path="explore-all/:slug" element={<ExploreAll />} />
              <Route path="resources" element={<Resources />} />
              <Route path="why-vistamark" element={<AboutUs />} />
              {/* <Route path="about" element={<AboutUs />} /> */}
              <Route path="digital-showroom" element={<DigitalShowroom />} />
              <Route path="style-detail/:id" element={<StyleDetail />} />
              <Route
                path="careers"
                element={
                  <RecaptchaProvider>
                    <Careers />
                  </RecaptchaProvider>
                }
              />
              <Route
                path="warranty"
                element={
                  <RecaptchaProvider>
                    <Warranty />
                  </RecaptchaProvider>
                }
              />
              <Route path="privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="contact-us"
                element={
                  <RecaptchaProvider>
                    <Contact />
                  </RecaptchaProvider>
                }
              />
              <Route path="fly-pages/:id" element={<FlyPages />} />
            </Route>
            <Route
              element={
                <FullPageScrollLayout>
                  <Outlet />
                </FullPageScrollLayout>
              }
            >
              <Route
                path="/"
                element={
                  <RecaptchaProvider>
                    <Home />
                  </RecaptchaProvider>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </Suspense>
    </>
  );
};

export default App;
